import React, { useCallback, useEffect, useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import Button from 'react-bootstrap/Button';
import Alert from 'react-bootstrap/Alert';
import PForgotPasswordForm from '../PForgotPasswordForm';
import {
    useGoogleReCaptcha
} from 'react-google-recaptcha-v3';
import './style.scss';
import Password from '../Password';
import VerificationCodeInput from '../VerificationCodeInput';
import debounce from 'lodash/debounce';
import { captureMessage } from '@sentry/react';

const PReverifyForm = ({ setShow, reverifyData, setReverifyData }) => {
    const { executeRecaptcha } = useGoogleReCaptcha();

    const loginRememberme = JSON.parse(localStorage.getItem('rememberme') ?? false);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [showForgotPassword, setShowForgotPassword] = useState(false);
    const [code, setCode] = useState('');
    const [emailErrorOnSendCode, setEmailErrorOnSendCode] = useState('');
    const [emailError, setEmailError] = useState('');

    const onSubmitWithReCAPTCHA = async (e) => {
        e.preventDefault();
        if (!executeRecaptcha) {
            console.log('Execute recaptcha not yet available in login');
        } else {
            const token = await executeRecaptcha('signup');
            formik.setFieldValue('isHuman', token, false);
            formik.setFieldValue('code', code);
            formik.handleSubmit();
        }
    };

    const formik = useFormik({
        initialValues: {
            login: reverifyData.username,
            email: reverifyData.email,
            password: '',
            rememberMe: loginRememberme,
            isHuman: '',
            code: '',
        },
        validationSchema: Yup.object({
            login: Yup.string()
                .required('Username is required'),
            password: Yup.string()
                .required('Password is required'),
            email: Yup.string()
                .required('Email is required'),
            code: Yup.number()
                .typeError('Invalid email verification code')
                .required('Email verification code required')
            // isHuman: Yup.string().required('Verify that you are human'),
        }),
        onSubmit: async (values) => {
            setError('');
            setIsLoading(true);

            localStorage.setItem('rememberme', formik.values.rememberMe);

            if (formik.values.rememberMe) {
                localStorage.setItem('myusername', formik.values.login);
            } else {
                localStorage.setItem('myusername', '');
            }

            // localStorage.setItem('intended', window.location.pathname);
            const localTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
            await axios.post('/api/login', { ...values, tz: localTz })
                .then((response) => {
                    localStorage.setItem('loggedin_user', response.data.user.id);
                    localStorage.setItem('loggedin_access_token', response.data.token);
                    localStorage.setItem('user', JSON.stringify(response.data.user));
                    const intended = localStorage.getItem('intended');
                    let path;

                    if (intended && intended !== '/login') {
                        if (response.data.user.role == ROLES.SU) {
                            path = intended;
                        } else {
                            path = `/${response.data.user.login}`;
                        }
                    } else {
                        if (response.data.user.role == ROLES.SU) {
                            path = '/home';
                        } else {
                            path = `/${response.data.user.login}`;
                        }
                    }

                    setIsLoading(false);
                    location.href = path;
                })
                .catch((error) => {
                    console.log({ error });
                    if (error.response.status === 422) {
                        Object.keys(error.response.data.errors).forEach((key) => {
                            formik.errors[key] = error.response.data.errors[key][0];
                        });
                    } else {
                        setError('Invalid username or password');
                    }
                    setIsLoading(false);
                });
        },
    });

    const handleShowForgotPassword = (e) => {
        e.preventDefault();
        setShowForgotPassword(true);
    };

    const cancelReverify = (e) => {
        e.preventDefault();
        setReverifyData(null);
        setShow('login');
    };

    const handleEmailChange = (e) => {
        formik.setFieldValue('email', e.target.value, false);
        setEmailError('');
    };

    const checkEmail = useCallback(
        debounce(async (value) => {
            if (value === '') {
                return;
            }

            await axios.post('/api/validate/email', {
                email: value,
                username: reverifyData.username
            }).then((response) => {
                formik.setErrors({ email: '' });
                setEmailError('');
            }).catch((error) => {
                captureMessage(error);
                if (error.response && error.response.status === 422) {
                    setEmailError(error.response.data.errors.email[0]);
                    formik.setErrors({ email: error.response.data.errors.email[0] });
                }
            });
        }, 500),
        [],
    );

    const handleEmailFocusOut = (e) => {
        checkEmail(e.target.value);
    };

    return (
        <div className="reverify__form">
            <Alert variant="danger" show={!!error}>{error}</Alert>
            <form onSubmit={onSubmitWithReCAPTCHA}>
                <div className={`form-group${formik.errors.login && formik.touched.login ? ' has-error' : ''}`}>
                    <input
                        name="login"
                        id="login"
                        type="text"
                        placeholder="Username"
                        className={`form-control${formik.errors.login && formik.touched.login ? ' is-invalid' : ''}`}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values.login}
                    />
                    {formik.errors.login && formik.touched.login ? <div className="invalid-feedback">{formik.errors.login}</div> : null}
                </div>
                <div className={`form-group${formik.errors.email && formik.touched.email ? ' has-error' : ''}`}>
                    <input
                        name="email"
                        id="email"
                        type="email"
                        placeholder="Email"
                        className={`form-control${((formik.errors.email && formik.touched.email)) ? ' is-invalid' : ''}`}
                        onChange={handleEmailChange}
                        onBlur={handleEmailFocusOut}
                        value={formik.values.email}
                        autoComplete="off"
                    />
                    {
                        emailErrorOnSendCode
                            ? <div className="invalid-feedback">{emailErrorOnSendCode}</div>
                            : (
                                emailError ?? (formik.errors.email && formik.touched.email)
                                    ? <div className="invalid-feedback">{emailError && formik.errors.email}</div>
                                    : null
                            )
                    }
                </div>
                <div className={`form-group ${formik.errors.code && formik.touched.code ? 'has-error' : ''}`}>
                    <VerificationCodeInput
                        toVerify={formik.values.email}
                        executeRecaptcha={executeRecaptcha}
                        code={code}
                        setCode={setCode}
                        emailError={emailError ?? formik.errors.email}
                        verificationError={formik.errors.code}
                        hasEmail={formik.values.email.length}
                        from="reverify"
                        // emailErrorOnSendCode={emailErrorOnSendCode}
                        setEmailErrorOnSendCode={setEmailErrorOnSendCode}
                    />
                </div>
                <div className={`form-group${formik.errors.password && formik.touched.password ? ' has-error' : ''}`}>
                    <Password
                        name="password"
                        id="password"
                        placeholder="Password"
                        customClass={`${formik.errors.password && formik.touched.password ? ' is-invalid' : ''}`}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values.password}
                        bottomOnly={true}
                        preicon={false}
                        style={{
                            borderRadius: '32px 0 0 32px',
                        }}
                        actionStyle={{
                            borderRadius: '0 32px 32px 0',
                            border: '1px solid #333',
                            background: '#1a1a1a',
                            color: '#fcfcfc'
                        }}
                    />
                    {formik.errors.password && formik.touched.password ? <div className="invalid-feedback">{formik.errors.password}</div> : null}
                </div>

                <div className="reverify-btns">
                    <Button
                        variant="info"
                        disabled={isLoading}
                        className="btn theme-btn theme-btn-2 btn-block"
                        onClick={cancelReverify}
                    >
                        Cancel
                    </Button>
                    <Button
                        type="submit"
                        variant="info"
                        disabled={isLoading}
                        className="btn theme-btn theme-btn-2 btn-block"
                    >
                        {isLoading ? 'Submitting' : 'Submit'}
                    </Button>
                </div>

                <div className="flex reverify__form-footer">
                    <div className="reverify__form-footer__container">
                        <div className="form-group form-check text-blue mb-0 remember-me">
                            <input
                                type="checkbox"
                                className="form-check-input"
                                id="rememberMe"
                                name="rememberMe"
                                onChange={formik.handleChange}
                                checked={[true, 'true'].includes(formik.values.rememberMe)}
                                value={formik.values.rememberMe}
                            />
                            <label className="form-group text-blue mb-0 form-check-label" htmlFor="rememberMe">Remember me</label>
                        </div>
                        <div className='forgot-password'>
                            <a
                                id="forgetpassDiv"
                                className="btn-text text-blue"
                                href="#!"
                                onClick={handleShowForgotPassword}
                            >
                                Forgot Password?
                            </a>
                        </div>
                    </div>
                </div>
            </form>
            <PForgotPasswordForm show={showForgotPassword} setShow={setShowForgotPassword} />
        </div>
    );
};

export default React.memo(PReverifyForm);
